import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ClientLifecycleStage, LIFECYCLE_STAGES } from '@/types/crm';
import { X, Loader2 } from 'lucide-react';

const ALL_STAGES: ClientLifecycleStage[] = [
  'lead', 'opportunity', 'interest', 'deck_sent', 'closing', 'committed', 'active', 'churned', 'paused',
];

interface BulkActionBarProps {
  selectedCount: number;
  onTransition: (toStage: ClientLifecycleStage) => Promise<void>;
  onCancel: () => void;
}

export function BulkActionBar({ selectedCount, onTransition, onCancel }: BulkActionBarProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targetStage, setTargetStage] = useState<ClientLifecycleStage | ''>('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (!targetStage) return;
    setIsProcessing(true);
    await onTransition(targetStage);
    setIsProcessing(false);
    setDialogOpen(false);
    setTargetStage('');
  };

  return (
    <>
      {/* Sits above the mobile bottom tab bar (56px) + home-indicator safe area;
          back to a simple bottom float on desktop where there is no tab bar */}
      <div className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-[calc(100vw-1rem)] bg-card border shadow-lg rounded-lg px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-3 sm:gap-4">
        <span className="text-sm font-medium whitespace-nowrap">{selectedCount} selected</span>
        <Button className="h-11 sm:h-9" onClick={() => setDialogOpen(true)}>Move to Stage</Button>
        <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-9 sm:w-9" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move {selectedCount} clients</DialogTitle>
          </DialogHeader>
          <Select value={targetStage} onValueChange={(v) => setTargetStage(v as ClientLifecycleStage)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a stage" />
            </SelectTrigger>
            <SelectContent>
              {ALL_STAGES.map(stage => (
                <SelectItem key={stage} value={stage}>
                  {LIFECYCLE_STAGES[stage].label} — {LIFECYCLE_STAGES[stage].description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!targetStage || isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
